/**
 * Encompass milestone webhook — pcb-loans-api v3/loans/admin/encompass/webhook (0.193.0, Bill 2026-11-03).
 * Encompass posts each loan's "milestone" event to pcb-loans-api, signed with the Key Vault key; the receiver files the
 * post-closing task for that one loan at once, through the same path as the 5-minute check (which stays on as the safety net).
 * Subscribing and unsubscribing need a reason and are audited. The signing key is never shown.
 *
 * House pattern (src/modules/System/SettingsPage.tsx): status as label/value rows, actions in the Card's actions slot, each
 * action confirmed in a ModalShell with the change reason, a dismissible toast on success.
 */

import { useCallback, useEffect, useState } from 'react';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { LoansApiError, loansApi } from '../loansApi';
import { errorText, fmtCt } from '../writeups/common';

interface WebhookEvent {
  eventId: string;
  loanGuid: string | null;
  eventType: string;
  eventTimeUtc: string | null;
  receivedUtc: string;
  outcome: string;
  note: string | null;
}

interface WebhookStatus {
  enabled: boolean;
  subscriptionId: string | null;
  endpointDefault: string;
  subscriptions: { status: number; body: unknown };
  events: WebhookEvent[];
  note: string | null;
}

type Action = 'subscribe' | 'replace' | 'unsubscribe';

const ACTION_COPY: Record<Action, { title: string; explain: string; confirm: string }> = {
  subscribe: {
    title: 'Subscribe to milestone events',
    explain: 'Ask Encompass to send every loan milestone event here, signed with the bank’s webhook key.',
    confirm: 'Subscribe',
  },
  replace: {
    title: 'Re-subscribe to milestone events',
    explain: 'Remove the current Encompass subscription and create a fresh one in its place.',
    confirm: 'Re-subscribe',
  },
  unsubscribe: {
    title: 'Unsubscribe from milestone events',
    explain: 'Remove the Encompass subscription. Milestone events stop arriving; the 5-minute check keeps filing tasks.',
    confirm: 'Unsubscribe',
  },
};

function modalError(e: unknown): string {
  if (e instanceof LoansApiError && e.message) return e.message.charAt(0).toUpperCase() + e.message.slice(1);
  return errorText(e);
}

function outcomePill(outcome: string): JSX.Element {
  const o = outcome.toLowerCase();
  const tone = o === 'error' || o === 'failed' ? 'err' : o === 'filed' || o === 'ok' ? 'ok' : 'muted';
  return <Pill tone={tone}>{outcome}</Pill>;
}

const eventColumns: ReadonlyArray<DataTableColumn<WebhookEvent>> = [
  { header: 'Received', cell: (r) => fmtCt(r.receivedUtc), sortKey: (r) => r.receivedUtc },
  { header: 'Event', cell: (r) => r.eventType, sortKey: (r) => r.eventType },
  {
    header: 'Loan',
    cell: (r) => (r.loanGuid ? <code className="cell-mono">{r.loanGuid.slice(0, 8)}</code> : <span className="cell-dim">—</span>),
  },
  { header: 'Outcome', cell: (r) => outcomePill(r.outcome), sortKey: (r) => r.outcome },
  { header: 'Note', cell: (r) => (r.note ? <span className="cell-desc">{r.note}</span> : <span className="cell-dim">—</span>) },
];

export function EncompassWebhookPanel(): JSX.Element {
  const [st, setSt] = useState<WebhookStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setSt(await loansApi.get<WebhookStatus>('v3/loans/admin/encompass/webhook'));
    } catch (e) {
      setLoadError(errorText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (a: Action): void => {
    setPending(a);
    setReason('');
    setTried(false);
    setActionError(null);
  };

  const close = (): void => {
    if (busy) return;
    setPending(null);
    setActionError(null);
  };

  const confirm = async (): Promise<void> => {
    setTried(true);
    if (!pending || reasonProblem(reason, { asciiOnly: true })) return;
    setBusy(true);
    setActionError(null);
    try {
      const why = asciiReason(reason);
      if (pending === 'unsubscribe') {
        await loansApi.send('DELETE', 'v3/loans/admin/encompass/webhook', {}, why);
        setToast('Unsubscribed. Encompass no longer sends milestone events; the 5-minute check carries on.');
      } else {
        const r = await loansApi.send<{ subscriptionId: string }>(
          'POST',
          'v3/loans/admin/encompass/webhook/subscribe',
          pending === 'replace' ? { replace: true } : {},
          why,
        );
        setToast(`Subscribed (subscription ${r.subscriptionId}). Encompass now sends each loan’s milestone events here.`);
      }
      setPending(null);
      await load();
    } catch (e) {
      setActionError(modalError(e));
    } finally {
      setBusy(false);
    }
  };

  const actions = st && (
    <div className="card-actions-row">
      {!st.subscriptionId && (
        <Button variant="primary" onClick={() => open('subscribe')}>
          Subscribe
        </Button>
      )}
      {st.subscriptionId && <Button onClick={() => open('replace')}>Re-subscribe</Button>}
      {st.subscriptionId && (
        <Button variant="danger" onClick={() => open('unsubscribe')}>
          Unsubscribe
        </Button>
      )}
    </div>
  );

  return (
    <>
      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card title="Milestone webhook" actions={actions || undefined}>
        <p className="form-field__hint">
          Encompass tells us the moment a loan completes a milestone, so its post-closing task is filed within seconds. The 5-minute
          check keeps running as the safety net, and a loan is never filed twice. Whether a task is actually filed follows the
          post-closing check's mode (off, shadow or live).
        </p>
        {loadError && (
          <Banner tone="err">
            Could not load the webhook status: {loadError} <Button onClick={() => void load()}>Retry</Button>
          </Banner>
        )}
        {!st && !loadError && <Spinner label="Loading the webhook status…" />}
        {st && (
          <>
            <dl className="detail-grid">
              <dt className="detail-grid__label">Receiving</dt>
              <dd className="detail-grid__value">{st.enabled ? <Pill tone="ok">on</Pill> : <Pill tone="warn">off</Pill>}</dd>
              <dt className="detail-grid__label">Subscription</dt>
              <dd className={st.subscriptionId ? 'detail-grid__value detail-grid__value--mono' : 'detail-grid__value'}>
                {st.subscriptionId ?? <Pill tone="warn">None</Pill>}
              </dd>
              <dt className="detail-grid__label">Endpoint</dt>
              <dd className="detail-grid__value detail-grid__value--mono">{st.endpointDefault}</dd>
            </dl>
            {st.note && <Banner tone="warn">{st.note}</Banner>}

            <h3>Recent notifications</h3>
            <DataTable<WebhookEvent>
              columns={eventColumns}
              rows={st.events}
              rowKey={(r) => r.eventId}
              emptyState={<p className="empty">No notifications yet.</p>}
            />
          </>
        )}
      </Card>

      <ModalShell
        open={pending !== null}
        title={pending ? ACTION_COPY[pending].title : ''}
        onClose={close}
        width={560}
        actions={
          pending && (
            <>
              <Button onClick={close} disabled={busy}>
                Cancel
              </Button>
              <Button variant={pending === 'unsubscribe' ? 'danger' : 'primary'} onClick={() => void confirm()} disabled={busy}>
                {busy ? 'Working…' : ACTION_COPY[pending].confirm}
              </Button>
            </>
          )
        }
      >
        {pending && (
          <div className="form-stack">
            {actionError && <Banner tone="err">{actionError}</Banner>}
            <p>{ACTION_COPY[pending].explain}</p>
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>
    </>
  );
}
